/**
 * Lift2Fit — the "Help" conversation (migration 0035).
 *
 * Every account has this thread, so it is not a page people have to find: it sits
 * in the chat interface they already open. The two sides are the same shape —
 * the account writes here, the founder answers from the admin support surface —
 * and the only difference is a label on each message (`fromAdmin`).
 *
 * The thread is created by the API on the first read, so an account that has
 * never asked for help still sees the contact and can start a conversation.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, type SupportMessage, type SupportSession } from '@/lib/api';
import { Button } from '@/components/ui';
import { AlertIcon } from '@/lib/icons';
import { chatDayKey, chatDayLabel, ChatAvatar, ChatIcon } from '../chat';
import { EmptyState, LoadingPanel } from '../components';
import { useAsync } from '../hooks';
import { cn } from '@/lib/format';

function timeOf(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return '';
  return new Date(parsed).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Who wrote a platform message: the assistant, or a person on the team. */
function messageLabel(message: SupportMessage): string {
  if (message.authorKind === 'bot') return 'Lift2Fit Assistant';
  if (message.fromAdmin) return 'Help';
  return 'You';
}

function waitLabel(seconds: number | null): string {
  if (seconds === null || seconds < 60) return 'under a minute';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

/**
 * The state of the CURRENT help: with the assistant, waiting for a person, with
 * a named teammate, or finished. Without it a client cannot tell "nobody has
 * seen this yet" from "someone is typing".
 */
function SessionBanner({
  session,
  busy,
  note,
  onClose,
}: {
  session: SupportSession | null;
  busy: boolean;
  note?: string | null;
  onClose: () => void;
}) {
  if (!session) {
    return (
      <div
        className="flex flex-wrap items-center justify-between gap-2 rounded-chat border border-hairline bg-page-subtle px-3 py-2"
        data-testid="help-session"
        data-status="none"
      >
        <span className="text-[12px] text-ink-muted">
          No open conversation — send a message and we’ll start a new one.
        </span>
      </div>
    );
  }
  const tone =
    session.status === 'assigned'
      ? 'border-success/40 bg-surface'
      : session.status === 'queued'
        ? 'border-warning/50 bg-page-subtle'
        : 'border-accent bg-page-subtle';
  const headline =
    session.status === 'bot'
      ? 'Lift2Fit Assistant is on this'
      : session.status === 'queued'
        ? 'Waiting for a teammate'
        : `You’re chatting with ${session.staffName ?? 'the Lift2Fit team'}`;
  const detail =
    session.status === 'bot'
      ? 'The assistant answers from our help articles first. Reply “human” any time and a teammate takes over.'
      : session.status === 'queued'
        ? `Everyone is with another client. You’re ${
            session.queuePosition === 1 ? 'first' : `#${session.queuePosition}`
          } in the queue — a teammate replies here as soon as they’re free.`
        : 'A person from the Lift2Fit team owns this conversation.';
  return (
    <div
      className={cn('flex flex-wrap items-start justify-between gap-2 rounded-chat border px-3 py-2', tone)}
      data-testid="help-session"
      data-status={session.status}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[12px] font-bold text-ink">{headline}</span>
        <span className="text-[11.5px] leading-snug text-ink-muted">{detail}</span>
        {note ? <span className="text-[11px] text-danger">{note}</span> : null}
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="rounded-pill"
        disabled={busy}
        data-testid="help-session-close"
        onClick={onClose}
      >
        {busy ? 'Closing…' : 'This is solved — close'}
      </Button>
    </div>
  );
}

export function HelpConversation({
  testId = 'chat-help',
  /** The page that hosts this pane already names the thread; chat does not. */
  withIdentity = true,
}: {
  testId?: string;
  withIdentity?: boolean;
}) {
  const state = useAsync(() => api.support(), []);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const messages: SupportMessage[] = state.data?.messages ?? [];
  const session = state.data?.session ?? null;

  // Keep the newest message in view by scrolling the LIST: `scrollIntoView`
  // would also scroll the page under the thread on a tall screen.
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages.length]);

  // 0038 — the assignment, the queue place and the assistant's answer all arrive
  // after a send, so the open help pane polls like the chat thread does.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      state.reload();
    }, 10_000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [closing, setClosing] = useState(false);
  const [sessionNote, setSessionNote] = useState<string | null>(null);

  const closeSession = async () => {
    setClosing(true);
    setSessionNote(null);
    const result = await api.closeSupportSession();
    setClosing(false);
    if (result.problem) {
      setSessionNote(result.problem.detail ?? 'That conversation could not be closed.');
      return;
    }
    state.reload();
  };

  const send = async (event: FormEvent) => {
    event.preventDefault();
    const body = draft.trim();
    if (body === '') return;
    setBusy(true);
    setError(null);
    const result = await api.sendSupportMessage(body);
    setBusy(false);
    if (result.problem || !result.data) {
      setError(result.problem?.detail ?? 'That message did not send. Try again.');
      return;
    }
    setDraft('');
    state.reload();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3" data-testid={testId}>
      <SessionBanner
        session={session}
        busy={closing}
        note={sessionNote}
        onClose={() => void closeSession()}
      />

      {withIdentity ? (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-chat border border-hairline bg-page-subtle px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-on-accent">
            <ChatIcon className="h-4 w-4" />
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-[15px] font-bold text-ink">Help</span>
            <span className="truncate text-[11.5px] text-ink-muted">
              The platform team answers here, in this thread — no separate inbox.
            </span>
          </div>
        </div>
        {state.data ? (
          <span className="rounded-pill border border-hairline bg-surface px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">
            {state.data.status === 'closed' ? 'Closed' : 'Open'}
          </span>
        ) : null}
      </div>
      ) : null}

      {state.loading && !state.data ? (
        <LoadingPanel label="Opening your help thread" rows={2} />
      ) : state.error && !state.data ? (
        <EmptyState
          title="We could not open the help thread"
          body={state.error}
          icon={<AlertIcon className="h-8 w-8 text-accent" />}
        />
      ) : messages.length === 0 ? (
        <EmptyState
          title="Ask us anything"
          body="Account, billing, a bug, or how something works — write it here and the team will answer in this thread."
          icon={<ChatIcon className="h-8 w-8 text-accent" />}
        />
      ) : (
        <ul
          ref={listRef}
          className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto px-1"
          data-testid={`${testId}-messages`}
        >
          {messages.map((message, index) => {
            const previous = messages[index - 1];
            const firstInRun = !previous || previous.fromAdmin !== message.fromAdmin;
            const newDay = !previous || chatDayKey(previous.createdAt) !== chatDayKey(message.createdAt);
            return (
              <li key={message.id} data-testid={`${testId}-message-${message.id}`}>
                {newDay ? (
                  <div
                    className="mb-2 flex items-center gap-3"
                    data-testid={`${testId}-day-separator`}
                  >
                    <span className="h-px flex-1 bg-hairline" />
                    <span className="rounded-pill border border-hairline bg-surface px-3 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                      {chatDayLabel(message.createdAt)}
                    </span>
                    <span className="h-px flex-1 bg-hairline" />
                  </div>
                ) : null}
                <div
                  className={cn(
                    'flex items-end gap-2',
                    message.fromAdmin ? 'justify-start' : 'justify-end',
                    firstInRun ? 'mt-2' : 'mt-0.5',
                  )}
                >
                {message.fromAdmin ? (
                  <span className="w-7 shrink-0">
                    <ChatAvatar
                      name={message.authorKind === 'bot' ? 'Lift2Fit Assistant' : 'Help'}
                      size="sm"
                    />
                  </span>
                ) : null}
                <div className={cn('flex max-w-[78%] flex-col gap-1', message.fromAdmin ? 'items-start' : 'items-end')}>
                  {firstInRun ? (
                    <span className="px-1 text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">
                      {messageLabel(message)}
                    </span>
                  ) : null}
                  <span
                    className={cn(
                      'flex flex-col gap-1 whitespace-pre-wrap px-3.5 py-2.5 text-[13.5px] leading-relaxed text-ink',
                      message.fromAdmin
                        ? 'rounded-bubble rounded-bl-chat-tail bg-page-subtle'
                        : 'rounded-bubble rounded-br-chat-tail bg-bubble-mine',
                    )}
                  >
                    <span>{message.body}</span>
                    <span
                      className={cn(
                        'l2f-tabular font-mono text-[9.5px] leading-none text-ink-muted',
                        message.fromAdmin ? 'self-start' : 'self-end',
                      )}
                    >
                      {timeOf(message.createdAt)}
                    </span>
                  </span>
                </div>
                </div>
              </li>
            );
          })}
          <div ref={endRef} />
        </ul>
      )}

      <form
        onSubmit={send}
        className="flex flex-col gap-2 rounded-chat-lg border border-hairline bg-surface p-2 shadow-float"
        data-testid={`${testId}-form`}
      >
        <label className="flex flex-col gap-1">
          <span className="sr-only">Message Help</span>
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={2}
            maxLength={4000}
            data-testid={`${testId}-input`}
            placeholder="Type your message…"
            className="l2f-chat-well w-full resize-none rounded-chat px-3.5 py-2.5 text-[14px] text-ink outline-none placeholder:text-ink-muted focus:ring-1 focus:ring-accent"
          />
        </label>
        <div className="flex items-center justify-between gap-2 px-1">
          <span className="text-[10.5px] text-ink-muted" data-testid={`${testId}-hint`}>
            {error ?? 'Visible to the Lift2Fit team only.'}
          </span>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            className="rounded-pill"
            disabled={busy || draft.trim() === ''}
            data-testid={`${testId}-send`}
          >
            Send
          </Button>
        </div>
      </form>
    </div>
  );
}
